import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Logo from './Logo';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: ${props => {
    // Always black on pages other than homepage
    if (props.location !== '/') {
      return 'rgba(0, 0, 0, 0.9)';
    }
    // On homepage, transparent until scrolled
    return props.scrolled ? 'rgba(0, 0, 0, 0.9)' : 'transparent';
  }};
  transition: background-color 0.3s ease;
  padding: 1rem 0;
  border-bottom: 1px solid transparent; /* Ensures consistent bottom edge */
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const NavLinks = styled.nav`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: rgba(0, 0, 0, 0.95);
    padding: 2rem;
    gap: 1.5rem;
  }
`;

const NavLink = styled(Link)`
  color: var(--white);
  font-weight: 500;
  position: relative;
  text-decoration: none;
  
  &:after {
    content: '';
    position: absolute;
    width: ${props => props.active ? '100%' : '0'};
    height: 2px;
    bottom: -5px;
    left: 0;
    background-color: var(--primary);
    transition: width 0.3s ease;
  }
  
  &:hover:after {
    width: 100%;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--white);
  font-size: 1.5rem;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const ContactButton = styled(motion.button)`
  background-color: var(--primary);
  color: var(--black);
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-2px);
  }
`;

const ResourcesItem = styled.div`
  position: relative;
  &:hover > div, &:focus-within > div { display: block; }
  @media (max-width: 768px) { width: 100%; }
`;

const ResourcesTrigger = styled(Link)`
  color: var(--white);
  font-weight: 500;
  position: relative;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: .4rem;
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 5px; }
  @media (max-width: 768px) { padding: .25rem 0; }
`;

const ResourceMenu = styled.div`
  display: none;
  position: absolute;
  top: calc(100% - 1px);
  left: -1rem;
  min-width: 250px;
  padding: .5rem;
  background: #111;
  border: 1px solid var(--gray-700);
  border-radius: 6px;
  box-shadow: 0 12px 28px rgba(0,0,0,.3);
  z-index: 10;
  a { display: block; padding: .75rem; border-radius: 4px; color: var(--white); }
  a:hover, a:focus-visible { color: var(--primary); background: rgba(255,255,255,.07); outline: none; }
  a[aria-current='page'] { color: var(--primary); background: rgba(255,255,255,.07); }
  @media (max-width: 768px) {
    position: static;
    display: ${props => props.$open ? 'block' : 'none'};
    margin-top: .5rem;
    box-shadow: none;
  }
`;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Helper: close menu on nav link click (mobile)
  const handleNavClick = () => {
    if (isOpen) setIsOpen(false);
  }
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  
  return (
    <HeaderContainer scrolled={scrolled} location={location.pathname}>
      <NavContainer>
        <Link to="/">
          <Logo color="white" height="40px" fullName={false} />
        </Link>
        
          <MobileMenuButton aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'} aria-expanded={isOpen} onClick={toggleMenu}>
          {isOpen ? '✕' : '☰'}
        </MobileMenuButton>
        
        <NavLinks isOpen={isOpen}>
          <NavLink to="/" active={location.pathname === '/' ? 1 : 0} onClick={handleNavClick}>Home</NavLink>
          <NavLink to="/services" active={location.pathname === '/services' ? 1 : 0} onClick={handleNavClick}>Services</NavLink>
          <NavLink to="/use-cases" active={location.pathname.startsWith('/use-cases') ? 1 : 0} onClick={handleNavClick}>Use Cases</NavLink>
          <ResourcesItem onMouseEnter={() => setResourcesOpen(true)} onMouseLeave={() => setResourcesOpen(false)}>
            <ResourcesTrigger to="/resources/sustainable-ai-framework" active={location.pathname.startsWith('/resources') ? 1 : 0} onClick={handleNavClick} aria-haspopup="true" aria-expanded={resourcesOpen}>
              Resources
            </ResourcesTrigger>
            <ResourceMenu $open={resourcesOpen || isOpen} role="menu">
              <Link role="menuitem" to="/resources/sustainable-ai-framework" aria-current={location.pathname === '/resources/sustainable-ai-framework' ? 'page' : undefined} onClick={() => { handleNavClick(); setResourcesOpen(false); }}>Framework</Link>
              <Link role="menuitem" to="/resources/techniques-tools" aria-current={location.pathname === '/resources/techniques-tools' ? 'page' : undefined} onClick={() => { handleNavClick(); setResourcesOpen(false); }}>Techniques &amp; Tools</Link>
            </ResourceMenu>
          </ResourcesItem>
          <NavLink to="/about" active={location.pathname === '/about' ? 1 : 0} onClick={handleNavClick}>About</NavLink>
          <NavLink to="/book" active={location.pathname === '/book' ? 1 : 0} onClick={handleNavClick}>Book</NavLink>
          
          <Link to="/contact" onClick={handleNavClick}>
            <ContactButton 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Contact Us
            </ContactButton>
          </Link>
        </NavLinks>
      </NavContainer>
    </HeaderContainer>
  );
};

export default Header;
